import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { appointmentsApi, clientsApi, extractList } from '../../api/facades';

const AppointmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: clientsRes } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list({}) });
  const clients = extractList(clientsRes);

  const { data: res, isLoading } = useQuery({
    queryKey: ['appointments', clientId, dateFrom],
    queryFn: () => appointmentsApi.list({ clientId: typeof clientId === 'number' ? clientId : undefined, dateFrom: dateFrom || undefined }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => appointmentsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setDeleteId(null);
      toast.success('Appointment deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    {
      key: 'date',
      label: 'Date',
      render: (r: any) => {
        if (!r.date) return '—';
        const d = new Date(r.date);
        if (Number.isNaN(d.getTime())) return String(r.date);
        return d.toLocaleDateString(undefined, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    { key: 'time', label: 'Time' },
    { key: 'client', label: 'Client', render: (r: any) => r.client?.name ?? r.advo_client_id ?? '—' },
  ];

  return (
    <div>
      <PageHeader hideTitle subtitle="Manage appointments." primaryAction={{ label: 'New', to: '/dashboard/appointments/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
        <select value={clientId === '' ? '' : clientId} onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm">
          <option value="">All clients</option>
          {clients.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm" placeholder="From date" />
      </div>
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No appointments." onRowClick={(id) => navigate(`/dashboard/appointments/${id}`)} onEdit={(id) => navigate(`/dashboard/appointments/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete appointment" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default AppointmentsListPage;
